import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import { Button, CircularProgress, Grid, Typography } from '@material-ui/core';
import SimpleTable from 'components/table/SimpleTable';
import { DashboardHelpers } from 'utils';

import _ from 'lodash';
import { useDevices, useEvents, useFields, useOrganizations, useProperties, useSettings } from 'hooks';
import moment from 'moment';
import EventTableHeader from './EventTableHeader';
import MobileTable from 'components/table/MobileTable';
import useMediaQuery from '@material-ui/core/useMediaQuery';

const useStyles = makeStyles((theme) => ({
  root: {},
  content: {
    padding: 0,
    paddingBottom: '0px !important',
    overflowY: 'auto',
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
  },
  avatar: {
    height: 42,
    width: 42,
    marginRight: theme.spacing(1),
  },
  actions: {
    padding: theme.spacing(1),
    justifyContent: 'flex-end',
  },
  visibleSmall: {
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  readColumn: { height: '30px', width: '120px', textAlign: 'right' },
  readButton: { marginLeft: '40px' },
  noneFound: { textAlign: 'center', padding: 10, backgroundColor: '#eee' },
  readEvent: { backgroundColor: theme.palette.background.default },
  centerCircle: { marginLeft: 'auto', marginRight: 'auto', display: 'block' },
}));

const EventsTable = ({ redirectData, setSelectedEvent, selectedEvent, setShowDetails, setSelectedId }) => {
  const { updateEvent, showArchive, events, isFetchingEvents, setFilter, filters } = useEvents();
  const { devices } = useDevices();
  const { fields } = useFields();
  const { properties } = useProperties();
  const { organizations } = useOrganizations();
  const { currentUserPermissions } = useSettings();
  let flatListOfEvents = [];
  if (!isFetchingEvents && (events || redirectData?.redirectEvents)) {
    const eventsToFlatten = redirectData?.redirectEvents ? redirectData?.redirectData?.events : events;
    if (eventsToFlatten) {
      _.forEach(eventsToFlatten, (eventBucket) => {
        flatListOfEvents = flatListOfEvents.concat(...eventBucket);
      });
    }
  }
  let listEvents = flatListOfEvents.length > 0 ? flatListOfEvents : [];
  const mobileView = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const { t } = useTranslation();
  const classes = useStyles();

  const [selectedEvents, setSelectedEvents] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);
  let selectAllChecked = false;

  const refreshedSelectedEvents = [];
  _.forEach(selectedEvents, (selectedEvent) => {
    const event = _.find(listEvents, { id: selectedEvent.id });
    refreshedSelectedEvents.push(event);
  });

  const handleSelect = (event, obj) => {
    if (!!selectedEvent && selectedEvent.id !== obj.id) {
      setSelectedEvent(obj);
      setSelectedId(obj.id);
      setShowDetails(true);
    } else {
      setSelectedEvent({});
      setSelectedId('');
      setShowDetails(false);
    }
  };

  const handleArchive = (s) => {
    updateEvent({ id: s.id, upadated_at: moment(new Date()).toISOString(), is_archived: !s.is_archived });
  };

  const todayOrYesterday = (date) => {
    const today = moment(new Date()).format('MM/DD/YYYY');
    let yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday = moment(yesterday).format('MM/DD/YYYY');
    const dateMoment = moment(date).utc().format('MM/DD/YYYY');

    if (dateMoment == today) {
      return t('today');
    } else if (dateMoment == yesterday) {
      return t('yesterday');
    } else {
      return DashboardHelpers.formatDateTime(dateMoment);
    }
  };
  const tableHeaders = [
    { alias: 'id', sortable: false, format: 'string', dataAlias: 'id', noDisplay: true },
    {
      alias: 'time',
      sortable: true,
      mobilePreview: true,
      mobileInfo: true,
      format: 'string',
      dataAlias: 'time',
      dataRender: (s) => {
        return (
          s.time && (
            <Typography className={classes.nameCell} align={'center'} variant="subtitle1">
              {todayOrYesterday(s.time)}
            </Typography>
          )
        );
      },
    },
    {
      alias: 'layer',
      sortable: true,
      mobileInfo: true,
      format: 'string',
      dataAlias: 'layer',
      className: 'hiddenXSmall',
      dataRender: (s) => (
        <Typography className={classes.nameCell} align={'center'}>
          {s.layer}
        </Typography>
      ),
    },
    {
      alias: 'source_identity',
      sortable: true,
      mobileInfo: true,
      format: 'string',
      dataAlias: 'layer_id',
      className: 'hiddenSmall',
      dataRender: (s) => {
        if (s.layer === 'device') {
          const device = _.find(devices, (device) => {
            const simpleHwAddress = device.hw_address?.replaceAll(':', '');
            return simpleHwAddress === s.data.device_id || simpleHwAddress === s.data.related;
          });
          return (
            <Typography className={classes.nameCell} align={'center'}>
              {device?.name
                ? device.name
                : device?.hw_address
                ? DashboardHelpers.trimHWAddress(device.hw_address)
                : s.data.device_id
                ? DashboardHelpers.trimHWAddress(s.data.device_id)
                : s.data.related
                ? DashboardHelpers.trimHWAddress(s.data.related)
                : t('unknown')}
            </Typography>
          );
        } else {
          const layer = DashboardHelpers.getObjectById(s.layer_id, organizations, properties, fields);
          return (
            <Typography className={classes.nameCell} align={'center'}>
              {layer?.name ? layer.name : layer.id}
            </Typography>
          );
        }
      },
    },
    {
      alias: 'type',
      sortable: true,
      mobileInfo: true,
      mobilePreview: true,
      format: 'string',
      dataAlias: 'type',
      className: 'hiddenMedium',
      dataRender: (s) => (
        <Typography className={classes.nameCell} align={'center'}>
          {s.type}
        </Typography>
      ),
    },
    {
      alias: 'organization_id',
      sortable: true,
      format: 'string',
      dataAlias: 'organization_id',
      className: 'hiddenSmall',
      dataRender: (s) => {
        const org = _.find(organizations, { id: s.organization_id });

        return (
          <Typography className={classes.nameCell} align={'center'}>
            {org ? org.name : s.organization_id}
          </Typography>
        );
      },
    },
    {
      alias: 'insight',
      sortable: true,
      mobileInfo: true,
      format: 'string',
      dataAlias: 'insight',
      className: 'hiddenSmall',
      dataRender: (s) => {
        return (
          s.data && (
            <Typography className={classes.nameCell} align={'center'}>
              {s.data.insight ? s.data.insight : s.data.error_message}
            </Typography>
          )
        );
      },
    },
    {
      alias: 'Archive',
      sortable: false,
      mobileInfo: true,
      format: 'string',
      dataAlias: 'archive',
      className: 'hiddenSmall isArchived ',
      allowClick: false,
      noDisplay: !_.includes(currentUserPermissions, 'archive'),
      dataRender: (s) => {
        return (
          s.data &&
          _.includes(currentUserPermissions, 'archive') && (
            <Grid container>
              <Grid item xs={6}>
                {!s.is_archived && <Button onClick={() => handleArchive(s)}>Archive Me</Button>}
                {s.is_archived && <Button onClick={() => handleArchive(s)}>Unarchive Me</Button>}
              </Grid>
            </Grid>
          )
        );
      },
    },
  ];
  if (!showArchive) {
    listEvents = _.filter(listEvents, (event) => {
      return !event.is_archived;
    });
  }

  if (
    _.differenceWith(listEvents.slice(page * pageSize, (page + 1) * pageSize), selectedEvents, (shown, selected) => {
      return shown.id === selected.id;
    }).length === 0 &&
    listEvents.length !== 0
  ) {
    selectAllChecked = true;
  } else {
    selectAllChecked = false;
  }

  const selectCheckbox = (event) => {
    if (!_.isEmpty(_.filter(selectedEvents, { id: event.id }))) {
      setSelectedEvents(
        _.filter(selectedEvents, (selectedEvent) => {
          return selectedEvent.id !== event.id;
        })
      );
    } else {
      setSelectedEvents(_.concat(selectedEvents, event));
    }
  };

  const selectAll = () => {
    if (selectAllChecked) {
      setSelectedEvents([]);
    } else {
      const pageEvents = _.union(selectedEvents, listEvents).slice(page * pageSize, (page + 1) * pageSize);
      setSelectedEvents(pageEvents);
    }
  };

  const selectEverything = () => {
    if (listEvents.length === selectedEvents.length) {
      setSelectedEvents([]);
    } else {
      setSelectedEvents(listEvents);
    }
  };
  const handleRequestSort = (filterType, property) => {
    if (filterType === 'setOrderBy') {
      setFilter({ orderBy: property });
    }
    if (filterType === 'setOrder') {
      setFilter({ order: property });
    }
  };

  return isFetchingEvents ? (
    <CircularProgress color="secondary" className={classes.centerCircle} />
  ) : mobileView ? (
    <MobileTable
      customHeader={() => {
        return (
          <EventTableHeader
            selectEverything={selectEverything}
            title={t('events')}
            data={listEvents}
            selected={_.map(selectedEvents, 'id')}
            selectAllChecked={selectAllChecked}
            pageSize={pageSize}
          />
        );
      }}
      tableHeader={tableHeaders}
      data={listEvents}
      selectRow={handleSelect}
    />
  ) : (
    <SimpleTable
      customHeader={() => {
        return (
          <EventTableHeader
            selectEverything={selectEverything}
            title={t('events')}
            data={listEvents}
            selected={_.map(selectedEvents, 'id')}
            selectAllChecked={selectAllChecked}
            pageSize={pageSize}
          />
        );
      }}
      tableHeader={tableHeaders}
      data={listEvents}
      isLoading={isFetchingEvents}
      selected={_.map(selectedEvents, 'id')}
      selectRow={handleSelect}
      rowStyle={classes.readEvent}
      showCheckbox
      selectCheckbox={selectCheckbox}
      pageSize={pageSize}
      setPageSize={setPageSize}
      setPage={setPage}
      selectAll={selectAll}
      selectAllChecked={selectAllChecked}
      showTableDetails
      selectEverything={selectEverything}
      defaultOrderBy={filters.orderBy}
      defaultOrder={filters.order}
      saveOrder={handleRequestSort}
      saveOrderBy={handleRequestSort}
    />
  );
};

EventsTable.propTypes = {
  redirectData: PropTypes.object,
  selectedEvent: PropTypes.object,
  setSelectedEvent: PropTypes.func,
  setShowDetails: PropTypes.func,
  setSelectedId: PropTypes.func,
};

EventsTable.defaultProps = {
  selectedEvent: {},
  redirectData: { redirectEvents: false, redirectData: undefined },
};

export default EventsTable;
